import React, {useEffect, Dispatch} from 'react'
import {useIntl} from 'react-intl'
import {useHistory} from 'react-router-dom'
import {HStack, VStack, Text, useMultiStyleConfig, StackProps, TextProps, List, ListProps} from '@chakra-ui/react'
import {ProductType} from '_types'
import {useAnalytics, useViewport} from '_customHooks'

interface SizeSelectorProps {
    product: ProductType
    selectedSize?: string | null
    isSizeListActive?: boolean
    sizeErrorVisible: boolean
    variant?: 'wishlist'
    setSizeErrorVisible: Dispatch<React.SetStateAction<boolean>>
    setSelectedSize: Dispatch<React.SetStateAction<string | null>>
    focusFirstSize?: boolean
}

/**
 * @name SizeSelector
 *
 * @component
 *
 * @description
 * Allows users to select a size for a given product.
 *
 * @remarks
 * Behaviour can be different based on variant; wishlist does not have automatic selection of size on overlay opening.
 *
 * @param {SizeSelectorProps} props - The properties for the `SizeSelector` component.
 * @param {ProductType} props.product - The product for which sizes are being selected.
 * @param {string | null} [props.selectedSize] - Optional selected size. Can be null if no size is selected.
 * @param {boolean} props.sizeErrorVisible - If should show error message for size selection.
 * @param {boolean} [props.isSizeListActive=true] - Indicates if size list is active for toggle behaviour on mobile devices.
 * @param {boolean} [props.variant] - Variant for the component.
 * @param {Dispatch<React.SetStateAction<boolean>>} props.setSizeErrorVisible - Function to update error state.
 * @param {Dispatch<React.SetStateAction<string | null>>} props.setSelectedSize - Function to update the selected size.
 *
 * @returns {React.FC<SizeSelectorProps>} The rendered `SizeSelector` component.
 */
const SizeSelector: React.FC<SizeSelectorProps> = ({
    product,
    selectedSize,
    isSizeListActive = true,
    sizeErrorVisible,
    variant,
    setSizeErrorVisible,
    setSelectedSize,
    focusFirstSize,
}) => {
    const {formatMessage} = useIntl()
    const {isMobile} = useViewport()
    const {analyticsEventCustom} = useAnalytics()
    const history = useHistory()
    const isDesktopEffective = variant === 'wishlist' ? true : !isMobile
    const styles = useMultiStyleConfig('SizeSelector', {variant}) as Record<string, StackProps & TextProps & ListProps>

    const variationGroups = product?.c_variationGroupsData
    const activeVariationGroup = variationGroups?.find((variation) => variation.default)
    const activeColorCode = activeVariationGroup?.color?.code

    const sizes =
        activeVariationGroup?.variants
            .filter((v) => v.online)
            .map((v) => ({
                id: v.ID,
                code: v.size.code,
                label: v.size.label,
                isInStock: v.availability?.inStock,
                isOnlyOneLeft: v.availability?.isOnlyOneLeft,
            })) || []

    const isMobileNotifyMe = !isDesktopEffective && activeVariationGroup?.notifyMeEnabled

    const availableSizeLabel = formatMessage({id: 'available.size', defaultMessage: 'Available sizes'})
    const requireProductLabel = formatMessage({id: 'button.offcanvas.request.size', defaultMessage: 'Request product'})
    const onlyOneLeftLabel = formatMessage({id: 'label.pdp.only.one.left', defaultMessage: 'Only one left'})

    const setSize = (sizeId: string, isAutoSelected?: boolean) => {
        setSelectedSize(sizeId)

        const activeSize = activeVariationGroup?.variants?.find((variant) => variant.ID === sizeId)?.size
        const activeSizeCode = activeSize?.code
        const activeSizeLabel = activeSize?.label
        const variationGroupId = activeSizeCode && sizeId.endsWith(activeSizeCode) ? sizeId.slice(0, -activeSizeCode.length) : sizeId

        if (isAutoSelected) {
            return
        }

        analyticsEventCustom({
            category: 'Size Selector',
            action: 'size select',
            label: `${variationGroupId} - ${activeSizeCode}`,
            buttonType: 'Quick Cart Add',
            accessType: variant !== 'wishlist' ? 'tile' : undefined,
        })
        // Dynamic Yield change attribute Tracking
        if (typeof window !== 'undefined' && window.DY && typeof window.DY.API === 'function') {
            try {
                window.DY.API('event', {
                    name: 'Change Attribute',
                    properties: {
                        dyType: 'change-attr-v1',
                        attributeType: 'size',
                        attributeValue: activeSizeLabel,
                    },
                })
            } catch (error) {
                console.error('[DY Tracking] Errore nel tracking attribute filter:', error)
            }
        }

        if (sizeErrorVisible) {
            setSizeErrorVisible(false)
        }
    }

    const autoFocus = (element: HTMLElement | null) => element?.focus()

    // "Request product" (mobile, out-of-stock size with notify-me) links to the product PDP (a PWA route):
    // navigate via React Router (SPA) so the datalayer survives and pwa_page fires, instead of a browser
    // hard navigation. The color/size query params are kept so the PDP restores the user's selection on
    // load/refresh/share. The group pdpUrl is already root-relative (backend contract).
    const goToRequestSizePdp = (sizeCode: string) => {
        const pdpUrl = activeVariationGroup?.pdpUrl
        if (!pdpUrl) return

        const [pathname, existingSearch] = pdpUrl.split('?')
        const search = new URLSearchParams(existingSearch)
        if (activeColorCode) search.set('color', activeColorCode)
        search.set('size', sizeCode)
        // Dedicated flag so the PDP knows to auto-open the Request Size (Notify Me) drawer. This is the
        // only entry point that sets it — color/size alone are ambiguous (also used for variant deep-links).
        // Mirrors the existing `addToCartAutomatic` query-param pattern.
        search.set('requestSize', 'true')

        history.push({pathname, search: `?${search.toString()}`})
    }

    useEffect(() => {
        if (variant !== 'wishlist') {
            // If we only have one size, select it
            if (sizes.length === 1) {
                setSize(sizes[0].id, true)
                if (sizeErrorVisible) {
                    setSizeErrorVisible(false)
                }
                return
            }
        }
    }, [sizes, isSizeListActive])

    return (
        sizes &&
        isSizeListActive && (
            <VStack {...styles.sizesRowWrapper} data-testid="size-selector">
                {isDesktopEffective && (
                    <Text {...styles.selectionTitle} {...(sizeErrorVisible ? styles.selectionTitleError : undefined)}>
                        {availableSizeLabel}
                    </Text>
                )}

                <List as={HStack} {...styles.sizesRow}>
                    {sizes?.map((size, idx) => (
                        <HStack
                            key={`${size.id}-${idx}`}
                            as="button"
                            tabIndex={size.isInStock ? 0 : -1}
                            data-element-type={'size-button'}
                            {...styles.sizeLabelWrapper}
                            {...(!size.isInStock ? styles.sizeLabelOutOfStockWrapper : {})}
                            {...(size.id === selectedSize ? styles.selectedSizeLabelWrapper : {})}
                            onClick={() => size.isInStock && setSize(size.id)}
                            ref={focusFirstSize && idx === 0 ? autoFocus : undefined}
                            aria-pressed={!size.isInStock && !isMobileNotifyMe ? undefined : selectedSize === size.id}
                            {...(!size.isInStock && isMobileNotifyMe ? {'aria-disabled': true} : {})}
                            {...(!size.isInStock && !isMobileNotifyMe ? {disabled: true} : {})}
                        >
                            <Text {...styles.sizeLabel} {...(!size.isInStock ? styles.sizeLabelOutOfStock : {})}>
                                {size.label}
                            </Text>
                            {!isDesktopEffective && !size.isInStock && activeVariationGroup?.notifyMeEnabled && (
                                <Text as="span" {...styles.labelStockInfo} onClick={() => goToRequestSizePdp(size.code)}>
                                    {requireProductLabel}
                                </Text>
                            )}
                            {!isDesktopEffective && size.isInStock && size.isOnlyOneLeft && (
                                <Text as="span" {...styles.labelOnlyOneInfo}>
                                    {onlyOneLeftLabel}
                                </Text>
                            )}
                        </HStack>
                    ))}
                </List>
            </VStack>
        )
    )
}

export default SizeSelector
